import React from 'react';
import cn from 'classnames';
import styled from 'styled-components';
import { colors } from '../theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce } from 'lodash';
import { mongoToTrx, formatTrx } from '../utils';
import moment from 'moment';
import { isEmpty } from 'lodash';

// Note: With the introduction of pre-construction and post-construction stages philosophy
// we are gonna show red indicators only for tasks that belongs to the critical path.
// This could change in the future if business rules changes
// So from now on, moving a tasks that belongs to the critical path will show the red indicators
// the very next day and not on the same day.
// In the construction stages, the rules still are applied on the same day

export const checkIsHoliday = (date, holidays = []) => {
  if (!(date instanceof Date) || isNaN(date)) return false;
  const formatted = date?.toISOString().split('T')[0];
  return holidays?.includes(formatted);
};

/**
 * Determines the critical class based on date comparisons and cycle time type
 *
 * @param {Date|string} date - The date being rendered by the calendar component
 * @param {Date|string} criticalDate - The critical deadline date to compare against // TODO: deprecate
 * @param {Date|string} startDate - The current start date of the task.
 * @param {Array} [holidays=[]] - Optional array of holiday dates to exclude from calculations
 * @param {boolean} isCritical - Flag indicating whether the task belongs to the critical path.
 *
 * @returns {string} Returns the CSS class name or status indicating criticality level
 */
export const criticalClass = (date, criticalDate, startDate, holidays = [], isCritical) => {
  const classLabel = [];
  const isHolidayDate = checkIsHoliday(date, holidays);
  if (isCritical && startDate /*&& date.getTime() > criticalDate.getTime()*/ && date.getTime() >= startDate.getTime()) {
    if (date.getTime() === startDate.getTime()) {
      classLabel.push('react-datepicker__critical-start-day');
    } else {
      classLabel.push('react-datepicker__critical-day', 'rounded-0');
    }
  }

  if (holidays && isHolidayDate) {
    classLabel.push('react-datepicker__holiday-date');
    if (startDate && date.getTime() === startDate.getTime()) classLabel.push('react-datepicker__holiday-date-day');
  }

  return classLabel.join(' ');
};

export const criticalProposedDateClass = (date, dateSelected, taskDate, criticalDate, holidays = [], isCritical) => {
  const classLabel = [];
  const isHolidayDate = checkIsHoliday(date, holidays);
  if (formatTrx(date) === mongoToTrx(taskDate)) {
    const disabledClass = isHolidayDate ? 'cursor-default' : 'pointer-events-none';
    classLabel.push(disabledClass, 'text-muted');
  }
  if (isCritical && taskDate && date.getTime() > criticalDate.getTime() && date.getTime() >= dateSelected?.getTime()) {
    if (date.getTime() === dateSelected.getTime()) {
      classLabel.push('react-datepicker__critical-start-day');
    } else {
      classLabel.push('react-datepicker__critical-day rounded-0');
    }
  }

  if (holidays && isHolidayDate) {
    classLabel.push('react-datepicker__holiday-date');
    if (date.getTime() === dateSelected.getTime()) classLabel.push('react-datepicker__holiday-date-day');
  }

  return classLabel.join(' ');
};

export const showWarningCriticalMsg = (date, criticalDate, isCritical) =>
  date ? date.getTime() > criticalDate.getTime() : false;

const toggleWarningMsg = debounce(
  (showWarningMsg = false, showHolidayMsg = false, selectedDayIsCritical = false, selectedDayIsHoliday = false) => {
    const container = document.querySelector('.react-datepicker__CalendarInfo_container');
    const criticalEl = document.querySelector('.criticalMsg');
    const holidayEl = document.querySelector('.holidayMsg');

    const showContainer = showWarningMsg || showHolidayMsg || selectedDayIsCritical || selectedDayIsHoliday;

    container?.classList.toggle('react-datepicker_CalendarInfo-show', showContainer);
    criticalEl?.classList.toggle('react-datepicker_CalendarInfo-show', showWarningMsg || selectedDayIsCritical);
    holidayEl?.classList.toggle('react-datepicker_CalendarInfo-show', showHolidayMsg || selectedDayIsHoliday);
  },
  250
);

export const CriticalDay = (
  day,
  date,
  criticalDate,
  calendarDate,
  holidays = [],
  isCritical = false,
  expectedDate = false,
  cycleTimeType
) => {
  criticalDate = moment(isCritical ? calendarDate : criticalDate)
    .add(isCritical ? 0 : 1, 'day')
    .toDate();
  const isConstruction = cycleTimeType === 'construction';
  const showWarningMsg = isConstruction || isCritical ? showWarningCriticalMsg(date, criticalDate) : false;
  const showHolidayMsg = checkIsHoliday(date, holidays);
  const selectedDayIsCritical = showWarningCriticalMsg(calendarDate, criticalDate);
  const selectedDayIsHoliday = checkIsHoliday(calendarDate, holidays);
  const isClickDisabled = expectedDate && formatTrx(date) === mongoToTrx(expectedDate);

  return (
    <div
      onMouseEnter={() => toggleWarningMsg(showWarningMsg, showHolidayMsg)}
      onMouseLeave={() => toggleWarningMsg(showWarningMsg, showHolidayMsg, selectedDayIsCritical, selectedDayIsHoliday)}
      onClick={e => {
        if (isClickDisabled) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }}
    >
      {day}
    </div>
  );
};

const WarningCriticalMsg = ({
  date,
  criticalDate,
  holidays = [],
  hideWarningMsg = false,
  isCritical,
  cycleTimeType,
}) => {
  const showWarningMsg = showWarningCriticalMsg(date, criticalDate) && !hideWarningMsg;
  const showHolidayMsg = checkIsHoliday(date, holidays);
  const showCalendarInfo = showWarningMsg || showHolidayMsg;
  const isConstruction = cycleTimeType === 'construction';

  const icon = isCritical ? 'circle-exclamation' : 'triangle-exclamation';
  const iconClassName = isCritical ? 'text-danger' : 'text-yellow-100';
  const warningMessage = isCritical ? (
    <>
      Selecting this date might <b>delay the entire Job</b> finish date.
    </>
  ) : (
    <>
      Date beyond Job Cycle Time Finish. <b>This won't affect Job Cycle Time.</b>
    </>
  );

  if (!isConstruction && !isCritical && isEmpty(holidays)) return null;
  return (
    <CalendarInfo
      className={cn('react-datepicker__CalendarInfo_container', {
        'react-datepicker_CalendarInfo-show': showCalendarInfo,
      })}
    >
      {!hideWarningMsg && isConstruction && (
        <div
          className={cn('mt-2 react-datepicker_CalendarInfo-hide criticalMsg', {
            'react-datepicker_CalendarInfo-show': showWarningMsg,
          })}
        >
          <FontAwesomeIcon icon={icon} className={`${iconClassName} mr-1 mt-1`} />
          <p className="ml-1 pr-3 mb-0">{warningMessage}</p>
        </div>
      )}

      <HolidayMsgContainer
        className={cn('mt-2 react-datepicker_CalendarInfo-hide holidayMsg', {
          'react-datepicker_CalendarInfo-show': showHolidayMsg,
        })}
      >
        <HolidayIcon />
        <p className="ml-2 mb-0">Holiday</p>
      </HolidayMsgContainer>
    </CalendarInfo>
  );
};

const HolidayMsgContainer = styled.div`
  background-color: ${colors.purple100};
  color: ${colors.purple50};
  width: 90px;
  padding: 3px 10px;
  border-radius: 50px;
  align-content: center;
  font-weight: 400;
`;

const StarContainer = styled.span`
  display: flex;
  border-radius: 30px;
  background-color: ${colors.purple500};
  height: 15px;
  width: 15px;
  align-items: center;
  justify-content: center;
  margin-top: 1px;

  svg {
    margin: 0;
    color: ${colors.white} !important;
    font-size: 9px;
  }
`;

export const HolidayIcon = ({ className = '' }) => (
  <StarContainer className={className}>
    <FontAwesomeIcon icon="star" />
  </StarContainer>
);

const CalendarInfo = styled.div`
  background-color: #fff;
  border-top: 1px solid ${colors.gray};
  color: ${colors.gray400};
  font-size: 12px;
  margin: 10px 0 0 10px;
  padding-bottom: 10px;
  width: 220px;
  flex-direction: column;
`;

export default WarningCriticalMsg;
